// Import necessary styles and d3 library
import './styles/graphic.scss';
import * as d3 from 'd3';

const cellHeight = 3.5;
const axisLength = 7;

// Define a default export function that creates a treemap visualization
export default function (props, container, isMobile) {
  // Destructure properties from props
  const {
    data,
    annotations,
    initialWidth,
    initialHeight,
    width,
    height,
    margins,
  } = props;

  const graphicWidth = isMobile ? 150 : 200;
  const cellWidth = graphicWidth / 24;

  const rows = d3.max(data, d => d.week_id) + 1;
  const graphicHeight = cellHeight * rows;

  // Append an SVG element to the container with specified initial dimensions
  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', graphicHeight + margins.top + margins.bottom);

  const offsetX = isMobile ? initialWidth - graphicWidth : initialWidth / 2 - graphicWidth / 2;

  // Group for cells — groups CAN have children
  const heatmap = svg.append('g')
    .attr('id', 'heatmap-svg')
    .attr('height', graphicHeight)
    .attr('transform', `translate(${offsetX}, ${(margins.top / 2)})`);


  const scale_colors = ['#ffffff', '#F2F2F2', '#F1691B', '#801944', '#262626'];
  const color = d3.scaleSequential()
    .domain([0, 8])
    .interpolator(d3.interpolateRgbBasis(scale_colors));


  const xScale = d3.scaleLinear()
    .domain([0, 24])
    .range([0, graphicWidth]);

  const yScale = d3.scaleLinear()
    .domain([0, rows])
    .range([0, graphicHeight]);

  heatmap.selectAll('rect')
    .data(data)
    .join('rect')
    .attr('x', d => xScale(d.hour))
    .attr('y', d => yScale(d.week_id))
    .attr('width', cellWidth)
    .attr('height', cellHeight)
    .attr('fill', d => color(d.alert_count));

  renderAnnotations(annotations, container, initialWidth, graphicWidth, isMobile, yScale);

  renderDaylightPeriod(data, heatmap, xScale, yScale);

  renderYearLines(data, heatmap, xScale, yScale, graphicWidth);

  const latest_date = new Date(data[data.length - 1].date);
  // add 6 days to the latest date
  latest_date.setDate(latest_date.getDate() + 6);

  document.querySelector('#date-update').innerHTML = formatDate(latest_date);
}

function renderAnnotations(annotations, container, initialWidth, graphicWidth, isMobile, yScale) {
  const width = isMobile ? ((initialWidth - graphicWidth) - axisLength - axisLength / 2) : ((initialWidth - graphicWidth) / 2) - axisLength - axisLength / 2;
  -axisLength - axisLength / 2;
  annotations.forEach((annotation) => {
    container.append('div')
      .attr('class', `annotation-div ${annotation.align}`)
      .html(annotation.text)
      .attr('style', `top: ${yScale(annotation.date) + cellHeight * 2}px; width: ${width}px;`);

    // container.select('#heatmap-svg').append('line')
    //   .attr('class', 'annotation-text')
    //   .attr('x1', 0)
    //   .attr('y1', yScale(annotation.date))
    //   .attr('x2', -10)
    //   .attr('y2', yScale(annotation.date))
    //   .attr('stroke', '#8e8e8e')
    //   .attr('stroke-width', 1);
  });
}

function renderDaylightPeriod(data, heatmap, xScale, yScale) {
  const sun_data = data
    .filter(d => d.hour === 0)
    .sort((a, b) => a.week_id - b.week_id);

  const sunrise_line = d3.line()
    .x(d => xScale(d.sunrise_hour))
    .y(d => yScale(d.week_id))
    .curve(d3.curveCatmullRom.alpha(1));

  heatmap.append('path')
    .datum(sun_data)
    .attr('d', sunrise_line)
    .attr('fill', 'none')
    .attr('stroke', '#262626')
    .attr('stroke-width', 1);

  const sunset_line = d3.line()
    .x(d => xScale(d.sunset_hour))
    .y(d => yScale(d.week_id))
    .curve(d3.curveCatmullRom.alpha(1));

  heatmap.append('path')
    .datum(sun_data)
    .attr('d', sunset_line)
    .attr('fill', 'none')
    .attr('stroke', '#262626')
    .attr('stroke-width', 1);

  const sun_anno_week = 67;
  const sun_anno_data = data.filter(d => d.week_id === sun_anno_week)[0];
  console.log(sun_anno_data);

  const arrowhead_width = 8;
  const x_offset = 2;

  heatmap.selectAll('line.sunrise-annotation')
    .data([sun_anno_data])
    .join('line')
    .attr('class', 'sunrise-annotation')
    .attr('x1', d => xScale(d.sunrise_hour) + arrowhead_width + x_offset)
    .attr('x2', d => xScale(d.sunset_hour) - arrowhead_width - x_offset)
    .attr('y1', yScale(sun_anno_week))
    .attr('y2', yScale(sun_anno_week))
    .attr('stroke', '#262626')
    .attr('stroke-width', 0.7);

  const svg_arrowhead = `M 0 0 L ${arrowhead_width} ${arrowhead_width / 2} L 0 ${arrowhead_width} Z`;

  heatmap.append('path')
    .datum(sun_anno_data)
    .attr('d', svg_arrowhead)
    .attr('fill', '#6E6E6E')
    .attr('stroke', 'none')
    .attr('transform', `translate(${xScale(sun_anno_data.sunrise_hour) + arrowhead_width + x_offset}, ${yScale(sun_anno_week) + arrowhead_width / 2}) rotate(180)`);

  heatmap.append('path')
    .datum(sun_anno_data)
    .attr('d', svg_arrowhead)
    .attr('fill', '#6E6E6E')
    .attr('stroke', 'none')
    .attr('transform', `translate(${xScale(sun_anno_data.sunset_hour) - arrowhead_width - x_offset}, ${yScale(sun_anno_week) - arrowhead_width / 2}) rotate(0)`);

  heatmap.append('text')
    .datum(sun_anno_data)
    .attr('class', 'sunrise-annotation-text')
    .attr('x', d => xScale(13))
    .attr('y', yScale(sun_anno_week - 2))
    .text('Daylight hours')
    .attr('fill', '#6E6E6E')
    .attr('font-weight', '500')
    .attr('font-size', 12)
    .attr('text-anchor', 'middle');
}

function renderYearLines(data, heatmap, xScale, yScale, graphicWidth) {
  const yearStarts = d3.nest()
    .key(d => d.year)
    .rollup(values => values[0]) // first row for each year
    .entries(data);

  heatmap.selectAll('line.year-line')
    .data(yearStarts)
    .join('line')
    .attr('class', 'year-line')
    .attr('x1', -axisLength)
    .attr('x2', graphicWidth + axisLength)
    .attr('y1', d => yScale(d.value.week_id))
    .attr('y2', d => yScale(d.value.week_id))
    .attr('stroke', '#8E8E8E')
    .attr('stroke-width', 1);

  heatmap.selectAll('text.year-text')
    .data(yearStarts)
    .join('text')
    .attr('class', 'year-text')
    .attr('x', -axisLength - axisLength / 2)
    .attr('y', d => yScale(d.value.week_id) + cellHeight)
    .text(d => (d.key == 2022 ? 'Feb. 28, 2022' : d.key))
    .attr('fill', '#8E8E8E')
    .attr('font-size', 12)
    .attr('text-anchor', 'end');

  // const lastWeekId = d3.max(data, d => d.week_id);
  // const lastWeekData = data.filter(d => d.week_id === lastWeekId && d.hour === 0);

  // heatmap.selectAll('line.year-line-end')
  //   .data(lastWeekData)
  //   .join('line')
  //   .attr('class', 'year-line-end')
  //   .attr('x1', -axisLength)
  //   .attr('x2', graphicWidth + axisLength)
  //   .attr('y1', d => yScale(d.week_id))
  //   .attr('y2', d => yScale(d.week_id))
  //   .attr('stroke', '#8E8E8E')
  //   .attr('stroke-width', 1);

  // heatmap.selectAll('text.year-text-end')
  //   .data(lastWeekData)
  //   .join('text')
  //   .attr('class', 'year-text-end')
  //   .attr('x', -axisLength - axisLength / 2)
  //   .attr('y', d => yScale(d.week_id) + cellHeight)
  //   .text(d => formatDate(new Date(d.date)))
  //   .attr('fill', '#8E8E8E')
  //   .attr('font-weight', '300')
  //   .attr('font-size', 12)
  //   .attr('text-anchor', 'end');
}

function formatDate(date) {
  return d3.timeFormat('%B %-d, %Y')(date)
    .replace('January', 'Jan.')
    .replace('February', 'Feb.')
    .replace('March', 'Mar.')
    .replace('April', 'Apr.')
    .replace('May', 'May')
    .replace('June', 'June')
    .replace('July', 'July')
    .replace('August', 'Aug.')
    .replace('September', 'Sept.')
    .replace('October', 'Oct.')
    .replace('November', 'Nov.')
    .replace('December', 'Dec.');
}
